// validationHelpers.js
import html2pdf from 'html2pdf.js';

// Required field validation
export const validateRequired = (value, fieldName, setErrors) => {
  if (value.trim() === "") {
    setErrors((prev) => ({ ...prev, [fieldName]: true }));
    return "This field is required";
  }
  setErrors((prev) => ({ ...prev, [fieldName]: false }));
  return "";
};

// validate unit number
export const validateUnitNumber = (value, setErrors, setErrorMessages) => {
  if (!value) {
    // Check for empty or whitespace-only
    setErrors((prev) => ({ ...prev, unitNumber: true }));
    setErrorMessages((prev) => ({
      ...prev,
      unitNumber: "Unit number is required",
    }));
    return "Unit number is required";
  }

  // Clear any existing errors
  setErrors((prev) => ({ ...prev, unitNumber: false }));
  setErrorMessages((prev) => ({ ...prev, unitNumber: "" }));
  return "";
};

// validate unit size
export const validateUnitNumberSize = (value, setErrors, setErrorMessages) => {
  if (!value) {
    setErrors((prev) => ({ ...prev, unitNumberSize: true }));
    setErrorMessages((prev) => ({
      ...prev,
      unitNumberSize: "Unit Size is required",
    }));
    return "Unit Size is required";
  }

  // If the field is not empty, clear any existing errors
  setErrors((prev) => ({ ...prev, unitNumberSize: false }));
  setErrorMessages((prev) => ({ ...prev, unitNumberSize: "" })); // Clear message if valid
  return "";
};

//validate account name
export const validateAccountName = (value, setErrors, setErrorMessages) => {
  if (!value) {
    setErrors((prev) => ({ ...prev, accountingName: true }));
    setErrorMessages((prev) => ({
      ...prev,
      accountingName: "Account Name is required",
    }));
    return "Account Name is required";
  }

  // Optionally check for length (e.g., minimum 2 characters)
  if (value.length < 2) {
    setErrors((prev) => ({ ...prev, accountingName: true }));
    setErrorMessages((prev) => ({
      ...prev,
      accountingName: "Account Name must be at least 2 characters",
    }));
    return "Account Name must be at least 2 characters";
  }

  setErrors((prev) => ({ ...prev, accountingName: false }));
  setErrorMessages((prev) => ({ ...prev, accountingName: "" })); // Clear message if valid
  return "";
};

// validate expiry date
export const validateExpiryDate = (value, setErrors, setErrorMessages) => {
  // Check if the field is empty
  if (!value) {
    setErrors((prev) => ({ ...prev, expMonth: true }));
    setErrorMessages((prev) => ({
      ...prev,
      expMonth: "Expiry date is required",
    }));
    return "Expiry date is required";
  }

  // Check if the input length is exactly 5
  if (value.length !== 5) {
    setErrors((prev) => ({ ...prev, expMonth: true }));
    setErrorMessages((prev) => ({
      ...prev,
      expMonth: "Expiry date must be in MM/YY format",
    }));
    return "Expiry date must be in MM/YY format";
  }

  const [monthStr, yearStr] = value.split("/");
  const month = parseInt(monthStr, 10);
  const year = parseInt(yearStr, 10) + 2000; // Assuming 2-digit year

  // Validate month and year
  if (isNaN(month) || isNaN(year) || month < 1 || month > 12) {
    setErrors((prev) => ({ ...prev, expMonth: true }));
    setErrorMessages((prev) => ({ ...prev, expMonth: "Invalid expiry date" }));
    return "Invalid expiry date";
  }

  const currentDate = new Date();
  const inputDate = new Date(year, month - 1);

  if (inputDate < currentDate) {
    setErrors((prev) => ({ ...prev, expMonth: true }));
    setErrorMessages((prev) => ({ ...prev, expMonth: "Card Expired" }));
    return "Card Expired";
  }

  // No errors
  setErrors((prev) => ({ ...prev, expMonth: false }));
  setErrorMessages((prev) => ({ ...prev, expMonth: "" })); // Clear any previous error message
  return "";
};

// validate zip code
export const validateZipCode = (value, setErrors, setErrorMessages) => {
  if (!value) {
    setErrors((prev) => ({ ...prev, billingZipCode: true }));
    setErrorMessages((prev) => ({
      ...prev,
      billingZipCode: "Zip code is required",
    }));
    return "Zip code is required";
  }

  if (value.length !== 5) {
    setErrors((prev) => ({ ...prev, billingZipCode: true }));
    setErrorMessages((prev) => ({
      ...prev,
      billingZipCode: "Zip code must be 5 digits",
    }));
    return "Zip code must be 5 digits";
  }
  setErrors((prev) => ({ ...prev, billingZipCode: false }));
  setErrorMessages((prev) => ({ ...prev, billingZipCode: "" })); // Clear any previous error message
  return "";
};

// validate routing number
export const validateRoutingNumber = (value, setErrors, setErrorMessages) => {
  if (!value) {
    setErrors((prev) => ({ ...prev, routingNumber: true }));
    setErrorMessages((prev) => ({
      ...prev,
      routingNumber: "Routing Number is required",
    }));
    return "Routing Number is required";
  }

  const isValid = /^\d{9}$/.test(value);
  if (!isValid) {
    setErrors((prev) => ({ ...prev, routingNumber: true }));
    setErrorMessages((prev) => ({
      ...prev,
      routingNumber: "Routing Number must be 9 digits",
    }));
    return "Routing Number must be 9 digits";
  }
  setErrors((prev) => ({ ...prev, routingNumber: false }));
  return "";
};

// validate email
export const validateEmail = (
  value,
  setErrors,
  setErrorMessages,
  errorKey = "email"   // 👈 supports nested keys like employerDetails.email
) => {
  const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
  const invalidPatterns = /(\.{2,}|@{2,}|@.*@)/;

  let email = "";

  // ✔ handle string or object
  if (typeof value === "string") {
    email = value.trim();
  } else if (
    typeof value === "object" &&
    value !== null &&
    typeof value.email === "string"
  ) {
    email = value.email.trim();
  } else {
    setErrors((prev) => ({ ...prev, [errorKey]: true }));
    setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "Invalid email format",
    }));
    return false;
  }

  // ❌ invalid email
  if (!email || !emailRegex.test(email) || invalidPatterns.test(email)) {
    setErrors((prev) => ({ ...prev, [errorKey]: true }));
    setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "Please enter a valid email address",
    }));
    return false;
  }

  // ✅ valid email → clear error
  setErrors((prev) => ({ ...prev, [errorKey]: false }));
  setErrorMessages((prev) => ({ ...prev, [errorKey]: "" }));

  return true;
};

export const validatePassword = (
  value,
  setErrors,
  setErrorMessages
) => {
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/;

  const trimmedValue = value.trim();

  // ❌ empty
  if (!trimmedValue) {
    setErrors((prev) => ({ ...prev, password: true }));
    setErrorMessages((prev) => ({
      ...prev,
      password: "Password is required",
    }));
    return true; // ❌ INVALID
  }

  // ❌ spaces
  if (trimmedValue.length !== value.length) {
    setErrors((prev) => ({ ...prev, password: true }));
    setErrorMessages((prev) => ({
      ...prev,
      password: "Spaces are not allowed",
    }));
    return true; // ❌ INVALID
  }

  // ❌ regex fail
  if (!passwordRegex.test(trimmedValue)) {
    setErrors((prev) => ({ ...prev, password: true }));
    setErrorMessages((prev) => ({
      ...prev,
      password:
        "Password must be at least 8 characters, contain one number, one uppercase letter, and one special character",
    }));
    return true; // ❌ INVALID
  }

  // ✅ valid
  setErrors((prev) => ({ ...prev, password: false }));
  setErrorMessages((prev) => ({ ...prev, password: "" }));
  return false; // ✅ VALID
};


// validate account number
export const validateAccountNumber = (value, setErrors, setErrorMessages) => {
  if (!value) {
    setErrors((prev) => ({ ...prev, accountingNumber: true }));
    setErrorMessages((prev) => ({
      ...prev,
      accountingNumber: "Account number is required",
    }));
    return "Account number is required";
  }

  const isValid = /^[0-9]{7,15}$/.test(value);
  if (!isValid) {
    setErrors((prev) => ({ ...prev, accountingNumber: true }));
    setErrorMessages((prev) => ({
      ...prev,
      accountingNumber: "Please enter a valid account number (7 to 15 digits)",
    }));
    return "Please enter a valid account number (7 to 15 digits)";
  }
  setErrors((prev) => ({ ...prev, accountingNumber: false }));
  setErrorMessages((prev) => ({ ...prev, accountingNumber: "" }));
  return "";
};

// validate creditcard
export const validateCreditCard = (value, setErrors, setErrorMessages) => {
  const formattedValue = value.replace(/\D/g, "");
  // Check if the field is empty
  if (formattedValue.length === 0) {
    setErrors((prev) => ({ ...prev, cardNumber: true }));
    setErrorMessages((prev) => ({
      ...prev,
      cardNumber: "Credit card number is required",
    }));
    return;
  }
  // Check if the length is not equal to 16
  if (formattedValue.length !== 16) {
    setErrors((prev) => ({ ...prev, cardNumber: true }));
    setErrorMessages((prev) => ({
      ...prev,
      cardNumber: "Credit card should have 16 digits",
    }));
    return;
  }
  // If valid
  setErrors((prev) => ({ ...prev, cardNumber: false }));
  setErrorMessages((prev) => ({ ...prev, cardNumber: "" }));
};

// validate cvv
export const validateCVV = (value, setErrors, setErrorMessages) => {
  if (!value) {
    setErrors((prev) => ({ ...prev, cvvNumber: true }));
    setErrorMessages((prev) => ({ ...prev, cvvNumber: "CVV is required" }));
    return;
  }
  if (value.length !== 3) {
    setErrors((prev) => ({ ...prev, cvvNumber: true }));
    setErrorMessages((prev) => ({
      ...prev,
      cvvNumber: "CVV must be 3 digits",
    }));
    return;
  }
  setErrors((prev) => ({ ...prev, cvvNumber: false }));
  setErrorMessages((prev) => ({ ...prev, cvvNumber: "" }));
  return;
};

// validate first name
export const validateFirstName = (value, setErrors?, setErrorMessages?, errorKey = "firstName") => {
  const nameRegex = /^[a-zA-Z ]+$/;
  let firstName;

  if (typeof value === "object" && value !== null && "firstName" in value) {
    firstName = value.firstName.trim();
  } else if (typeof value === "string") {
    firstName = value.trim();
  } else {
    if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: true }));
    if (setErrorMessages) setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "Invalid input format",
    }));
    return "Invalid input format";
  }

  if (firstName.length === 0) {
    if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: true }));
    if (setErrorMessages) setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "First name is required",
    }));
    return "First name is required";
  }
  if (!nameRegex.test(firstName)) {
    if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: true }));
    if (setErrorMessages) setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "First name should only contain letters & spaces",
    }));
    return "First name should only contain letters & spaces";
  }
  if (firstName.length < 2 || firstName.length > 20) {
    if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: true }));
    if (setErrorMessages) setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "First name should have 2-20 characters",
    }));
    return "First name should have 2-20 characters";
  }

  if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: false }));
  if (setErrorMessages) setErrorMessages((prev) => ({ ...prev, [errorKey]: "" }));
  return "";
};

// validate last name 
export const validateLastName = (value, setErrors?, setErrorMessages?, errorKey = "lastName") => {
  const nameRegex = /^[a-zA-Z ]+$/;
  let lastName;

  // Check if the value is an object and has lastName, or is a string
  if (typeof value === "object" && value !== null && "lastName" in value) {
    lastName = value.lastName.trim();
  } else if (typeof value === "string") {
    lastName = value.trim();
  } else {
    // Handle unexpected value types
    if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: true }));
    if (setErrorMessages) setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "Invalid input format",
    }));
    return "Invalid input format";
  }

  // Check if the last name is empty
  if (lastName.length === 0) {
    if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: true }));
    if (setErrorMessages) setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "Last name is required",
    }));
    return "Last name is required";
  }

  // Check if the last name matches the regex
  if (!nameRegex.test(lastName)) {
    if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: true }));
    if (setErrorMessages) setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "Last name should only contain letters & spaces",
    }));
    return "Last name should only contain letters & spaces";
  }

  // Check length constraints
  if (lastName.length < 2 || lastName.length > 20) {
    if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: true }));
    if (setErrorMessages) setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "Last name should have 2-20 characters",
    }));
    return "Last name should have 2-20 characters";
  }

  // Clear any previous errors
  if (setErrors) setErrors((prev) => ({ ...prev, [errorKey]: false }));
  if (setErrorMessages) setErrorMessages((prev) => ({ ...prev, [errorKey]: "" }));
  return "";
};

// validate name
export const validateName = (value, setErrors) => {
  const nameRegex = /^[a-zA-Z ]+$/; // Allows only letters and spaces
  let name;

  // Check if the value is a string or an object
  if (typeof value === "string") {
    name = value.trim();
  } else if (typeof value === "object" && value !== null && "name" in value) {
    name = value.name.trim();
  } else {
    // Handle unexpected value types
    setErrors((prev) => ({ ...prev, name: true }));
    return "Invalid name format";
  }

  // Check if the name is empty
  if (name.length === 0) {
    setErrors((prev) => ({ ...prev, name: true }));
    return "Name is required";
  }

  // Check if the name matches the regex
  if (!nameRegex.test(name)) {
    setErrors((prev) => ({ ...prev, name: true }));
    return "Please enter letters only";
  }

  // Check length constraints
  if (name.length < 2 || name.length > 40) {
    setErrors((prev) => ({ ...prev, name: true }));
    return "Name should have 2-40 characters";
  }

  // Clear any previous errors
  setErrors((prev) => ({ ...prev, name: false }));
  return "";
};

//validate phone number
export const validateNumber = (value, setErrors, setErrorMessages) => {
  const mobileNumberRegex = /^\+?[1-9]\d{7,14}$/; // Ensures 8-15 digit phone numbers

  let phoneNumber = "";

  // Extract phoneNumber safely
  if (value && typeof value === "object" && "phoneNumber" in value) {
    phoneNumber = value.phoneNumber?.trim() || "";
  } else if (typeof value === "string") {
    phoneNumber = value.trim();
  } else {
    setErrors((prev) => ({ ...prev, phoneNumber: true }));
    setErrorMessages((prev) => ({ ...prev, phoneNumber: "Invalid input format" }))
    return "Invalid input format";
  }

  // Remove all non-numeric characters except '+'
  phoneNumber = phoneNumber.replace(/[^\d+]/g, "");

  // Check if phoneNumber is empty
  if (!phoneNumber) {
    setErrors((prev) => ({ ...prev, phoneNumber: true }));
    setErrorMessages((prev) => ({ ...prev, phoneNumber: "Phone number is required" }))
    return "Phone number is required";
  }

  // Ensure phone number has more than just a country code
  if (/^\+\d{1,4}$/.test(phoneNumber)) {
    setErrors((prev) => ({ ...prev, phoneNumber: true }));
    setErrorMessages((prev) => ({ ...prev, phoneNumber: "Please enter the full phone number" }))
    return "Please enter the full phone number";
  }

  // Validate full phone number format
  if (!mobileNumberRegex.test(phoneNumber)) {
    setErrors((prev) => ({ ...prev, phoneNumber: true }));
    setErrorMessages((prev) => ({ ...prev, phoneNumber: "Invalid phone number" }))
    return "Invalid phone number";
  }

  // Validation passed
  setErrors((prev) => ({ ...prev, phoneNumber: false }));
  setErrorMessages((prev) => ({ ...prev, phoneNumber: "" }))
  return "";
};

// validate checkEmptyFeilds
export const checkEmptyFields = (fields, setErrors) => {
  const errors = {};
  Object.keys(fields).forEach((field) => {
    if (fields[field].trim() === "") {
      errors[field] = `[field] is required`;
      setErrors((prev) => ({ ...prev, [field]: true }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  });
  return errors;
};

// Positive number validation
export const validatePositiveNumber = (value, setErrors) => {

  const numberValue = parseFloat(value);
  if (numberValue <= 0) {
    setErrors((prev) => ({ ...prev, positiveNumber: true }));
    return "Must be a positive number";
  }
  setErrors((prev) => ({ ...prev, positiveNumber: false }));
  return "";
};

// Future date validation
export const validateDate = (dateString, setErrors) => {
  const date = new Date(dateString);
  const now = new Date();
  if (date <= now) {
    setErrors((prev) => ({ ...prev, futureDate: true }));
    return "Due date must be in the future";
  }
  setErrors((prev) => ({ ...prev, futureDate: false }));
  return "";
};

// Grace period validation
export const validateGracePeriod = (value, setErrors) => {
  const numberValue = parseInt(value, 10);
  if (!Number.isInteger(numberValue) || numberValue < 0) {
    setErrors((prev) => ({ ...prev, gracePeriod: true }));
    return "Grace period must be a non-negative integer";
  }
  setErrors((prev) => ({ ...prev, gracePeriod: false }));
  return "";
};

// Amount validation
export const validateAmount = (value, setErrors) => {
  const amountValue = parseFloat(value);
  if (amountValue <= 0) {
    setErrors((prev) => ({ ...prev, amount: true }));
    return "Amount must be greater than zero";
  }
  setErrors((prev) => ({ ...prev, amount: false }));
  return "";
};

export const checkEmptyFieldsFunc = (
  data: any,
  clearErrors: any,
  errors: any,
  errorMessages: any,
  setErrors: any,
  setErrorMessages: any,
  skipField: string[] = [],
  country?: any
): boolean => {
  clearErrors();
  const newErrors = { ...errors };
  const newErrorMessages = { ...errorMessages };
  let hasErrors = false;

  const checkFields = (item: any, parentKey: string = '') => {
    if (Array.isArray(item) && item.length === 0) {
    newErrors[parentKey] = true;
    newErrorMessages[parentKey] = `${parentKey.replace(/([A-Z])/g, ' $1')} is required`;
    hasErrors = true;
    return;
  }

  // ✅ BLOCK EMPTY OBJECT
  if (
    typeof item === 'object' &&
    item !== null &&
    !Array.isArray(item) &&
    Object.keys(item).length === 0
  ) {
    newErrors[parentKey] = true;
    newErrorMessages[parentKey] = `${parentKey.replace(/([A-Z])/g, ' $1')} is required`;
    hasErrors = true;
    return;
  }
   
    if (Array.isArray(item)) {
      item.forEach((item1, index) => {
        Object.keys(item1).forEach((key) => {
          const value = item1[key];
          const errorKey = `${key}[${index}]`;
          // Honor skipField for array rows (bare key or indexed). Object-branch already
          // skipped dotted paths; EmploymentHistory/References are List<UserBase*> and
          // previously ignored skipField entirely — backend-echoed nulls (e.g.
          // whatsAppOptInAt) permanently blocked Next after Back → refetch.
          if (skipField.includes(key) || skipField.includes(errorKey)) {
            newErrors[errorKey] = false;
            return;
          }

          // Check for empty fields
          if ((typeof value === 'string' && !value.trim()) || value === null || value === undefined) {
            newErrors[errorKey] = true;
            const errorMessage1 = `${key.replace(/([a-z])([A-Z])/g, "$1 $2")}`;
            const formattedErrorMessage = errorMessage1.charAt(0).toUpperCase() + errorMessage1.slice(1);
            newErrorMessages[errorKey] = `${formattedErrorMessage} is required`;
            hasErrors = true;
          }
          else if (key === 'firstName') {
            const errorMessage = validateFirstName(value, undefined, undefined, errorKey);
            if (errorMessage) {
              newErrors[errorKey] = true;
              newErrorMessages[errorKey] = errorMessage;
              hasErrors = true;
            } else {
              newErrors[errorKey] = false;
              newErrorMessages[errorKey] = "";
            }
          } else if (key === 'lastName') {
            const errorMessage = validateLastName(value, undefined, undefined, errorKey);
            if (errorMessage) {
              newErrors[errorKey] = true;
              newErrorMessages[errorKey] = errorMessage;
              hasErrors = true;
            } else {
              newErrors[errorKey] = false;
              newErrorMessages[errorKey] = "";
            }
          }
          // Phone number validation
          else if (key.toLowerCase().includes('phone')) {
            if (!value || !value.trim()) {
              newErrors[errorKey] = true;
              newErrorMessages[errorKey] = 'Phone number is required';
              hasErrors = true;
            } else {
              const isValid = validatePhone(value, country, setErrors, setErrorMessages, parentKey, index);
              if (!isValid) {
                newErrors[errorKey] = true;
                newErrorMessages[errorKey] = "Please enter a valid US phone number";
                hasErrors = true;
              } else {
                newErrors[errorKey] = false;
                newErrorMessages[errorKey] = "";
              }
            }
          }
          else {
            newErrors[errorKey] = false;
          }
        });
      });
    } else if (typeof item === 'object' && item !== null) {
      Object.keys(item).forEach((key) => {
        const value = item[key];
        const errorKey = parentKey ? `${parentKey}.${key}` : key;
         if (skipField.includes(errorKey)) {
      newErrors[errorKey] = false;
      return;
    }

        // Phone number validation
        if (key.toLowerCase().includes('phone')) {
          if (!value || !value.trim()) {
            newErrors[errorKey] = true;
            newErrorMessages[errorKey] = 'Phone number is required';
            hasErrors = true;
          }
          else {
            const isValid = validatePhone(value, country, setErrors, setErrorMessages, parentKey);
            if (!isValid) {
              newErrors[errorKey] = true;
              newErrorMessages[errorKey] = "Please enter a valid US phone number";
              hasErrors = true;
            } else {
              newErrors[errorKey] = false;
              newErrorMessages[errorKey] = "";
            }
          }
        } else if (key === 'firstName') {
          const errorMessage = validateFirstName(value, undefined, undefined, errorKey);
          if (errorMessage) {
            newErrors[errorKey] = true;
            newErrorMessages[errorKey] = errorMessage;
            hasErrors = true;
          } else {
            newErrors[errorKey] = false;
            newErrorMessages[errorKey] = "";
          }
        } else if (key === 'lastName') {
          const errorMessage = validateLastName(value, undefined, undefined, errorKey);
          if (errorMessage) {
            newErrors[errorKey] = true;
            newErrorMessages[errorKey] = errorMessage;
            hasErrors = true;
          } else {
            newErrors[errorKey] = false;
            newErrorMessages[errorKey] = "";
          }
        }
        else {
          checkFields(value, errorKey);
        }
      });
    } else {
      const errorKey = parentKey;
       if (skipField.includes(errorKey)) {
        newErrors[errorKey] = false;
        return;
      }

      if ((typeof item === 'string' && !item.trim()) || item === null || item === undefined) {
        newErrors[errorKey] = true;
        const errorMessage1 = `${errorKey.replace(/([a-z])([A-Z])/g, "$1 $2")}`;
        const formattedErrorMessage = errorMessage1.charAt(0).toUpperCase() + errorMessage1.slice(1);
        const result = `${formattedErrorMessage} is required`;
        newErrorMessages[errorKey] = `${result} `;
        hasErrors = true;
      } else {
        newErrors[errorKey] = false;
      }
    }
  };

  checkFields(data);
  setErrors(newErrors);
  setErrorMessages(newErrorMessages);

  // if (skipField === 'Questionnaires') {
  //   hasErrors = false;
  // }

  return hasErrors;
};
export const handleDownloadPdf = async (setISPrint, contentRef, fileName = "Review Application.pdf") => {
  setISPrint(true);
  
  // Wait for the UI to update and render in print mode
  await new Promise(resolve => setTimeout(resolve, 500));

  const element = contentRef.current;

  if (!element) {
    console.error("Content reference is null!");
    setISPrint(false);
    return;
  }

  // Apply temporary CSS to prevent blank pages and ensure margins match
  const style = document.createElement("style");
  style.innerHTML = `
    * {
      box-sizing: border-box;
    }
    
    .leaseAgreementSection-print, .leaseAgreementSection {
      page-break-inside: avoid !important; 
      overflow: visible !important;
      padding: 15px; /* Adds spacing inside sections */
      border: 1px solid transparent; /* Prevents content cutoff */
    }

    .leaseAgreementSection-print:first-child, .leaseAgreementSection:first-child {
      page-break-before: avoid !important;
    }

    .leaseAgreementHeader {
      page-break-after: avoid !important;
      margin-bottom: 10px; /* Adds spacing below header */
    }

    body {
      width: 100%;
      height: auto;
      margin: 20px; /* Adds space around document */
    }

    /* Prevent content from getting cut off */
    .pdf-content {
      display: block;
      width: 100%;
      max-width: 100%;
      overflow: visible !important;
      word-wrap: break-word;
    }
  `;
  document.head.appendChild(style);

  const options = {
    margin: 0.25,
    filename: fileName,
    image: { type: "jpeg", quality: 0.85 },
    html2canvas: { scale: 1.5, useCORS: true, letterRendering: true },
    jsPDF: { unit: "in", format: "letter", orientation: "portrait", compressPDF: true },
    pagebreak: { 
      mode: ["css", "legacy"],
      avoid: [
        ".emp_item", ".addressItem", ".rentpay_information", ".references-row", 
        ".question-row", ".signature-card", ".application_card_name", 
        ".creditcheck", ".common_application_card_header", ".leaseAgreementSection",
        ".leaseAgreementSection-print", "p", "h1", "h2", "h3", "h4", "h5", "h6", "img", "label",
        ".rentpaychecktype"
      ]
    },
  };

  try {
    await html2pdf().from(element).set(options).save();
  } catch (error) {
    console.error("PDF Generation Error:", error);
  } finally {
    document.head.removeChild(style);
    setTimeout(() => {
      setISPrint(false);
    }, 200);
  }
};

export const handleGenerateAndUploadPdf = async (setISPrint, contentRef, uploadCallback) => {
  setISPrint(true);
  
  // Wait for the UI to update and render in print mode (header, styling, etc.)
  await new Promise(resolve => setTimeout(resolve, 500));

  const element = contentRef.current; // Get the content to generate PDF from

  if (!element) {
    console.error("Content reference is null!");
    setISPrint(false);
    return;
  }

  // Apply temporary styles to ensure proper formatting
  const style = document.createElement("style");
  style.innerHTML = `
    * {
      box-sizing: border-box;
    }
    
    .leaseAgreementSection-print, .leaseAgreementSection {
      page-break-inside: avoid !important; 
      overflow: visible !important;
      padding: 15px; /* Adds spacing inside sections */
      border: 1px solid transparent; /* Prevents content cutoff */
    }

    .leaseAgreementSection-print:first-child, .leaseAgreementSection:first-child {
      page-break-before: avoid !important;
    }

    .leaseAgreementHeader {
      page-break-after: avoid !important;
      margin-bottom: 10px; /* Adds spacing below header */
    }

    body {
      width: 100%;
      height: auto;
      margin: 20px; /* Adds space around document */
    }

    /* Prevent content from getting cut off */
    .pdf-content {
      display: block;
      width: 100%;
      max-width: 100%;
      overflow: visible !important;
      word-wrap: break-word;
    }
  `;
  document.head.appendChild(style);

  const options = {
    margin: 0.25,
    image: { type: 'jpeg', quality: 0.85 },
    html2canvas: { scale: 1.5, useCORS: true, letterRendering: true },
    jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait', compressPDF: true },
    pagebreak: { 
      mode: ["css", "legacy"],
      avoid: [
        ".emp_item", ".addressItem", ".rentpay_information", ".references-row", 
        ".question-row", ".signature-card", ".application_card_name", 
        ".creditcheck", ".common_application_card_header", ".leaseAgreementSection",
        ".leaseAgreementSection-print", "p", "h1", "h2", "h3", "h4", "h5", "h6", "img", "label",
        ".rentpaychecktype"
      ]
    },
  };

  try {
    const pdfBlob = await html2pdf().from(element).set(options).outputPdf('blob');

    // Call the upload function with the generated PDF Blob and await it
    if (uploadCallback) {
      await uploadCallback(pdfBlob);
    }
  } catch (error) {
    console.error("PDF Generation Error:", error);
  } finally {
    document.head.removeChild(style);
    setTimeout(() => {
      setISPrint(false);
    }, 200);
  }
};


// export const handleGenerateAndUploadPdf = async (setISPrint, contentRef, uploadCallback) => {
//   setISPrint(true);
//   const element = contentRef.current; // Get the content to generate PDF from

//   const options = {
//     margin: 0.25,
//     image: { type: 'jpeg', quality: 0.50 },
//     html2canvas: { scale: 1.10 },
//     jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
//     pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
//   };

//   if (element) {
//     const pdfBlob = await html2pdf().from(element).set(options).outputPdf('blob');

//     // Call the upload function with the generated PDF Blob
//     if (uploadCallback) {
//       uploadCallback(pdfBlob);
//     }
//   }

//   setTimeout(() => {
//     setISPrint(false);
//   }, 200);
// };

export const validateDays = (fieldName, value, setErrors, setErrorMessages) => {
  const formattedFieldName = fieldName
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase());

  if (value === "" || value === null || value === undefined) {
    setErrors((prev) => ({ ...prev, [fieldName]: true }));
    setErrorMessages((prev) => ({
      ...prev,
      [fieldName]: `${formattedFieldName} is required`,
    }));
    return;
  }

  let numericValue = Number(value);

  if (isNaN(numericValue) || numericValue < 1 || numericValue > 31) {
    setErrors((prev) => ({ ...prev, [fieldName]: true }));
    const errorMessage =
      fieldName === "rentDueDay"
        ? "Day must be between 1 and 31"
        : `${formattedFieldName} must be between 1 and 31`;
    setErrorMessages((prev) => ({
      ...prev,
      [fieldName]: errorMessage,
    }));
    return;
  }

  // If valid, clear the error
  setErrors((prev) => ({ ...prev, [fieldName]: false }));
  setErrorMessages((prev) => ({
    ...prev,
    [fieldName]: "",
  }));
};

const countryPhoneLengths: { [key: string]: number } = {
  us: 11, ca: 11, au: 11, in: 12, gb: 11, de: 14, fr: 11, it: 12, es: 11, br: 13,
  mx: 13, ru: 11, cn: 13, jp: 12, kr: 11, sa: 12, ae: 12, ar: 12, za: 11, id: 15,
  tr: 11, ph: 12, my: 11, th: 11, vn: 11, ng: 13, pk: 12, bd: 15, ir: 12, nz: 11,
  co: 12, pe: 12, cl: 15, eg: 15, af: 15, al: 15, dz: 15, ad: 15, ao: 15, ag: 15,
  am: 11, aw: 15, at: 15, az: 12, bs: 15, bh: 15, bb: 15, by: 12, be: 11, bz: 15,
  bj: 15, bt: 15, bo: 15, ba: 15, bw: 15, io: 15, bn: 15, bg: 15, bf: 15, bi: 15,
  kh: 15, cm: 15, cv: 15, bq: 15, cf: 15, td: 15, km: 15, cd: 15, cg: 15, cr: 11,
  ci: 11, hr: 15, cu: 15, cw: 15, cy: 11, cz: 12, dk: 10, dj: 15, dm: 15, do: 15,
  ec: 15, sv: 11, gq: 15, er: 15, ee: 13, et: 15, fj: 15, fi: 15, gf: 15, pf: 15,
  ga: 15, gm: 15, ge: 15, gh: 15, gr: 15, gd: 15, gp: 15, gu: 15, gt: 11, gn: 15,
  gw: 15, gy: 15, ht: 11, hn: 15, hk: 11, hu: 15, is: 10, iq: 15, ie: 12, il: 13
};

const normalizeUSPhone = (phone: string): string => {
  const digitsOnly = phone.replace(/\D/g, "");
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return digitsOnly.slice(1);
  }
  return digitsOnly;
};

const isValidUSPhone = (phone: string): boolean => {
  const normalized = normalizeUSPhone(phone);
  // NANP: 10 digits, area/exchange code cannot start with 0 or 1.
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(normalized);
};

/** TEMP WhatsApp testing: Indian mobile = 10 digits starting 6-9, optional leading 91. */
const isValidIndianPhone = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, "");
  const national = digits.length === 12 && digits.startsWith("91")
    ? digits.slice(2)
    : digits.length === 10
      ? digits
      : "";
  return /^[6-9]\d{9}$/.test(national);
};

/** Loose E.164-ish check for non-US/IN during multi-country WhatsApp QA. */
const isValidInternationalPhoneLoose = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
};

export const validatePhone = (
  phone: any,
  country: any,
  setErrors: any,
  setErrorMessages: any,
  sectionName?: string,
  index?: number
) => {
  const inputPhone = typeof phone === "string" ? phone : `${phone ?? ""}`;
  // Empty/unknown country (e.g. OnboardingContext.countryCode default "") must stay US-strict.
  const rawCountry = (
    typeof country === "string"
      ? country
      : country?.countryCode || country?.iso2 || ""
  );
  const countryCode = (String(rawCountry).trim() || "us").toLowerCase();

  const errorKey = sectionName ? `${sectionName}.phoneNumber`
    : index !== undefined ? `phoneNumber[${index}]` : "phoneNumber";

  // TEMP multi-country WhatsApp QA. REMINDER: later restore USA-only validation for development.
  // Indian / loose-international paths only when country is explicitly non-US.
  // Do NOT auto-accept 91… numbers under country "us".
  if (countryCode === "in") {
    if (!isValidIndianPhone(inputPhone)) {
      setErrors((prev) => ({ ...prev, [errorKey]: true }));
      setErrorMessages((prev) => ({
        ...prev,
        [errorKey]: "Please enter a valid Indian phone number"
      }));
      return false;
    }
    setErrors((prev) => ({ ...prev, [errorKey]: false }));
    setErrorMessages((prev) => ({ ...prev, [errorKey]: "" }));
    return true;
  }

  if (countryCode !== "us") {
    if (!isValidInternationalPhoneLoose(inputPhone)) {
      setErrors((prev) => ({ ...prev, [errorKey]: true }));
      setErrorMessages((prev) => ({
        ...prev,
        [errorKey]: "Please enter a valid phone number"
      }));
      return false;
    }
    setErrors((prev) => ({ ...prev, [errorKey]: false }));
    setErrorMessages((prev) => ({ ...prev, [errorKey]: "" }));
    return true;
  }

  const sanitizedPhone = normalizeUSPhone(inputPhone);
  const expectedLength = 10;

  if (!sanitizedPhone || sanitizedPhone.length !== expectedLength) {
    setErrors((prev) => ({ ...prev, [errorKey]: true }));
    setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "Please enter a valid US phone number"
    }));
    return false;
  }
  if (!isValidUSPhone(sanitizedPhone)) {
    setErrors((prev) => ({ ...prev, [errorKey]: true }));
    setErrorMessages((prev) => ({
      ...prev,
      [errorKey]: "Please enter a valid US phone number"
    }));
    return false;
  }

  setErrors((prev) => ({ ...prev, [errorKey]: false }));
  setErrorMessages((prev) => ({ ...prev, [errorKey]: "" }));
  return true;
};
